<template>
  <div>
    <v-toolbar flat>
      <v-btn
        v-if="collapseable"
        icon
        @click="$emit('update:collapsed', !collapsed)"
      >
        <v-icon v-if="!collapsed">
          mdi-unfold-less-horizontal
        </v-icon>
        <v-icon v-else>
          mdi-unfold-more-horizontal
        </v-icon>
      </v-btn>
      <v-toolbar-title>Gesamtübersicht</v-toolbar-title>
      <v-spacer />
      <v-fade-transition>
        <v-toolbar-items v-show="!collapsed">
          <v-btn
            icon
            title="Excel-Tabelle herunterladen"
            :disabled="loading"
            :href="downloadExcelUrl"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
          <v-btn
            icon
            title="Aktualisieren"
            :disabled="loading"
            @click="get"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-fade-transition>
    </v-toolbar>
    <v-expand-transition>
      <v-data-table
        v-show="!collapsed"
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        fixed-header
        :height="dataTableHeight"
      >
        <template v-slot:item="props">
          <tr>
            <td :class="{'font-weight-bold': isSumRow(props.item[0])}">
              {{ getEmployeeNameByIndex(props.item[0]) }}
            </td>
            <td
              v-for="(project, i) in headers.slice(1)"
              :key="project.value"
              class="text-right pointer"
              :class="{'font-weight-bold': isSumRow(props.item[0]) || isSumCol(i + 1), highlighted: (highlighted !== null) && (highlighted.projectId === project.value) && (highlighted.employeeId === getEmployeeIdByIndex(props.item[0]))}"
              @click="onCellClick(project.value, getEmployeeIdByIndex(props.item[0]))"
            >
              {{ formatValue(props.item[i + 1]) }}
            </td>
          </tr>
        </template>
        <template v-slot:no-data>
          Keine Daten mit den gewählten Filterkriterien.
        </template>
      </v-data-table>
    </v-expand-transition>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {api, endpoints} from '@/api'
import {PaginationSortingMixin} from './PaginationSortingMixin'
import {formatFloat} from '../../filters/Number'

import {dataTableHeight} from "../utils/sizes"

export default {
  name: 'Pivot2',
  mixins: [PaginationSortingMixin],
  props: {
    collapseable: {
      type: Boolean,
      default: false,
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      items: [],
      headers: [],
      employees: [],
      loading: false,
      highlighted: null,
      options: {
        itemsPerPage: -1
      },
      dataTableHeight: dataTableHeight
    }
  },
  computed: {
    ...mapGetters([
      'selectedEmployees',
      'selectedPeriod',
      'selectedProjects',
      'selectedTenant',
    ]),
    filters () {
      const r = {
        tenant: this.selectedTenant,
        taskday__gte: this.selectedPeriod.start.format('YYYY-MM-DD'),
        taskday__lte: this.selectedPeriod.end.format('YYYY-MM-DD'),
      }
      if (this.selectedEmployees.length > 0) {
        r.employee__in = this.selectedEmployees.map(i => i.id).join(",")
      }
      if (this.selectedProjects.length > 0) {
        r.project__in = this.selectedProjects.map(i => i.id).join(",")
      }
      return r
    },

    downloadExcelUrl () {
      const url = endpoints.fetch_pivot().url
      const args = Object.assign({format: 'xlsx'}, {...this.filters, ...this.sortingOptions})
      let argString = ""
      for (let argKey in args) {
        argString += (argString.length > 0 ? '&' : '') + escape(argKey) + "=" + escape(args[argKey])
      }
      return `${url}?${argString}`
    },
  },
  watch: {
    filters: {
      handler () {
        this.get()
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    async get () {
      this.loading = true
      if (this.selectedTenant === null) {
        this.total = 0
        return
      }
      try {
        const r = await api(endpoints.fetch_pivot(), {...this.filters, ...this.sortingOptions, ...this.paginationOptions}, {method: 'GET'})
        if (r.ok) {
          const data = await r.json()

          //this.items.splice(0, this.items.length, ...this.items)

          const newHeaders = [{text: 'Mitarbeiter', value: '_employees'}, ...data.column.map(i => ({text: i[0], value: i[1] || null, sortable: !!i[1]}))]
          this.headers.splice(0, this.headers.length, ...newHeaders)

          const newItems = data.values.map((item, i) => [i, ...item])
          this.items.splice(0, this.items.length, ...newItems)

          this.total = this.getTotal(r) || this.items.length

          const newEmployees = data.row
          this.employees.splice(0, this.employees.length, ...newEmployees)
        } else {
          // TODO: error handling
        }
      } catch (err) {
        // TODO: error handling
      } finally {
        this.loading = false
      }
    },
    getEmployeeNameByIndex (index) {
      if (this.offsetIndex(index) < this.total && index < this.employees.length) {
        return this.employees[index][0]
      } else if (this.offsetIndex(index) === this.total - 1) {
        return "Summe"
      } else {
        throw new Error("Array index out of bounds")
      }
    },
    getEmployeeIdByIndex (index) {
      if (this.offsetIndex(index) < this.total && index < this.employees.length) {
        return this.employees[index][1]
      } else if (this.offsetIndex(index) === this.total - 1) {
        return null
      } else {
        throw new Error("Array index out of bounds")
      }
    },
    isSumRow (index) {
      return this.offsetIndex(index) === this.total - 1
    },
    isSumCol (index) {
      return this.offsetIndex(index) === this.headers.length - 1
    },
    offsetIndex (index) {
      return ((this.options.page - 1) * this.options.itemsPerPage) + index
    },
    formatValue (value) {
      return value !== null ? formatFloat(value) : value
    },
    onCellClick (projectId, employeeId) {
      const selection = {projectId: projectId, employeeId: employeeId}
      this.$set(this, 'highlighted', selection)
      this.$emit("selected", selection)
      this.$emit("update:collapseable", true)
    },
  },
}
</script>

<style lang="scss" scoped>
.highlighted {
  background-color: yellow;
}

.pointer {
  cursor: pointer;
}

td {
  background-color: transparent;
  transition: background-color .3s cubic-bezier(0.25, 0.8, 0.5, 1);

  &:hover {
    background-color: #dddddd;
  }
}
</style>
